<script lang="ts" setup>
import {
  CabinTypeLabel,
  RoofTypeLabel,
  DrivetrainLabel,
  EngineTypeLabel,
  SizeTypeLabel,
  TechDataVariations,
  TechDataVehicle,
} from '@/types';
import { WbButtonGroup, WbButtonGroupItem } from '../../ssr/wb';
import { useVehicleSelection } from '@/composables/useVehicleSelection';
import { getVehicleType } from '@/utils/vehicleType';
import { useTranslations } from '@/composables/useTranslations';
import { useTracking } from '@/composables/useTracking';
import { computed, watch, ref } from 'vue';

const props = defineProps<{
  sizes: SizeTypeLabel[];
  roofs: RoofTypeLabel[];
  cabins: CabinTypeLabel[];
  drivetrains: DrivetrainLabel[];
  batteryCapacities: number[];
  engines: EngineTypeLabel[];
  weights: number[];
  variations: TechDataVariations;
  vehicles: TechDataVehicle[];
  translations: Record<string, string>;
  modelSeries: string;
  selectedVariant?: TechDataVehicle;
}>();

const emit = defineEmits(['variant-selected']);

// Get vehicle type with a default value of Sprinter_Cabin if undefined
const vehicleType: 'Citan' | 'Vito' | 'Sprinter_Cabin' | 'Sprinter_Roof' =
  getVehicleType(props.variations) ?? 'Sprinter_Cabin';

// Initialize composables
const {
  selectedRoof,
  selectedCabin,
  selectedEngine,
  selectedBatteryCapacity,
  selectedWeight,
  selectedDrivetrain,
  variation,
  vehicle,
  available_weights,
  available_cabins,
  available_roofs,
  available_engines,
  available_batteryCapacities,
  available_drivetrains,
  selectCabin,
  selectRoof,
  selectEngine,
  selectBatteryCapacity,
  selectWeight,
  selectDrivetrain,
  setVehiclePropertiesBySize,
} = useVehicleSelection(
  vehicleType,
  props.variations,
  props.vehicles,
  props.selectedVariant,
);

const { trackFeatureSelection } = useTracking(props.modelSeries);
const {
  getSizeTypeTranslation,
  getRoofTypeTranslation,
  getCabinTypeTranslation,
  getDrivetrainTranslation,
  getEngineTypeTranslation,
  getComponentLabel,
} = useTranslations(props.translations);

// Watch for variant changes and emit updates
const selectedVehicle = computed(() => vehicle.value);
watch(
  selectedVehicle,
  (newVehicle) => {
    emit('variant-selected', newVehicle);
  },
  { immediate: true },
);
</script>
<template>
  <div class="owvans-technical-data-wrapper">
    <div class="wb-grid-container wb-grid-container--full-width">
      <div class="wb-grid-row">
        <!-- Size Variations -->
        <div
          class="wb-grid-col variant-selector wb-grid-col-mq1-12 wb-grid-col-mq3-12 wb-grid-col-mq4-6"
          v-if="variations.variations.length > 1"
        >
          <div class="owvans-technical-data-wrapper__column-desktop">
            <span class="owvans-technical-data-wrapper__column-text wb-text-s">
              {{
                getComponentLabel(
                  'owvc_tech_data_sizeTypeLabel',
                  'sizeTypeLabel',
                )
              }}
            </span>
            <div class="owvans-technical-data-wrapper__button-group">
              <WbButtonGroup
                variant="radio"
                :ssrAttrs="{ class: 'wbx-button-group' }"
              >
                <WbButtonGroupItem
                  v-for="(variationItem, index) in variations.variations"
                  :key="`size-button${index}`"
                  variant="secondary"
                  :selected="
                    variationItem.sizeType === vehicle?.sizeType || undefined
                  "
                  :data-test-id="`owvans-tech-data-variations-desktop-${variationItem.sizeType}`"
                  :ssrAttrs="{
                    class: {
                      'wbx-button wbx-button--large wbx-button--secondary': true,
                      'wbx-button--selected':
                        variationItem.sizeType === vehicle?.sizeType,
                    },
                  }"
                  @click="
                    setVehiclePropertiesBySize(variationItem);
                    trackFeatureSelection(
                      `size_type.${variationItem.sizeType}`,
                      vehicle ?? null,
                    );
                  "
                >
                  {{ getSizeTypeTranslation(variationItem.sizeType) }}
                </WbButtonGroupItem>
              </WbButtonGroup>
            </div>
          </div>
        </div>

        <!-- Roof Selector -->
        <div
          class="wb-grid-col roof-selector wb-grid-col-mq1-12 wb-grid-col-mq3-12 wb-grid-col-mq4-6"
          v-if="roofs.length > 1"
        >
          <div class="owvans-technical-data-wrapper__column-desktop">
            <span class="owvans-technical-data-wrapper__column-text wb-text-s">
              {{
                getComponentLabel(
                  'owvc_tech_data_roofTypeLabel',
                  'roofTypeLabel',
                )
              }}
            </span>
            <div class="owvans-technical-data-wrapper__button-group">
              <WbButtonGroup
                variant="radio"
                :ssrAttrs="{ class: 'wbx-button-group' }"
              >
                <WbButtonGroupItem
                  v-for="(roof, index) in roofs"
                  :key="index"
                  variant="secondary"
                  :selected="roof === vehicle?.roofHeightType || undefined"
                  :disabled="!available_roofs.includes(roof) || undefined"
                  :data-test-id="`owvans-tech-data-roofs-desktop-${roof}`"
                  :ssrAttrs="{
                    class: {
                      'wbx-button wbx-button--large wbx-button--secondary': true,
                      'wbx-button--selected': roof === vehicle?.roofHeightType,
                      'wbx-button--disabled': !available_roofs.includes(roof),
                    },
                  }"
                  @click="
                    selectRoof(roof);
                    trackFeatureSelection(`roof_type.${roof}`, vehicle ?? null);
                  "
                >
                  {{ getRoofTypeTranslation(roof) }}
                </WbButtonGroupItem>
              </WbButtonGroup>
            </div>
          </div>
        </div>

        <!-- Cabin Selector -->
        <div
          class="wb-grid-col roof-selector wb-grid-col-mq1-12 wb-grid-col-mq3-12 wb-grid-col-mq4-6"
          v-if="cabins.length > 1"
        >
          <div class="owvans-technical-data-wrapper__column-desktop">
            <span class="owvans-technical-data-wrapper__column-text wb-text-s">
              {{
                getComponentLabel(
                  'owvc_tech_data_cabinTypeLabel',
                  'cabinTypeLabel',
                )
              }}
            </span>
            <div class="owvans-technical-data-wrapper__button-group">
              <WbButtonGroup
                variant="radio"
                :ssrAttrs="{ class: 'wbx-button-group' }"
              >
                <WbButtonGroupItem
                  v-for="(cabin, index) in cabins"
                  :key="index"
                  variant="secondary"
                  :selected="cabin === vehicle?.cabinType || undefined"
                  :disabled="!available_cabins.includes(cabin) || undefined"
                  :data-test-id="`owvans-tech-data-cabins-desktop-${cabin}`"
                  :ssrAttrs="{
                    class: {
                      'wbx-button wbx-button--large wbx-button--secondary': true,
                      'wbx-button--selected': cabin === vehicle?.cabinType,
                      'wbx-button--disabled': !available_cabins.includes(cabin),
                    },
                  }"
                  @click="
                    selectCabin(cabin);
                    trackFeatureSelection(
                      `cabin_type.${cabin}`,
                      vehicle ?? null,
                    );
                  "
                >
                  {{ getCabinTypeTranslation(cabin) }}
                </WbButtonGroupItem>
              </WbButtonGroup>
            </div>
          </div>
        </div>

        <!-- Engine Selector -->
        <div
          class="wb-margin-bottom-3xs weight-selector wb-grid-col wb-grid-col-mq1-12 wb-grid-col-mq3-12 wb-grid-col-mq4-6"
          v-if="engines.length > 1"
        >
          <div class="owvans-technical-data-wrapper__column-desktop">
            <span class="owvans-technical-data-wrapper__column-text wb-text-s">
              {{ getComponentLabel('owvc_tech_data_engine', 'engine') }}
            </span>
            <div class="owvans-technical-data-wrapper__button-group">
              <WbButtonGroup
                variant="radio"
                :ssrAttrs="{ class: 'wbx-button-group' }"
              >
                <WbButtonGroupItem
                  v-for="(engine, index) in engines"
                  :key="index"
                  variant="secondary"
                  :selected="engine === vehicle?.engineType || undefined"
                  :disabled="!available_engines.includes(engine) || undefined"
                  :data-test-id="`owvans-tech-data-engines-desktop-${engine}`"
                  :ssrAttrs="{
                    class: {
                      'wbx-button wbx-button--large wbx-button--secondary': true,
                      'wbx-button--selected': engine === vehicle?.engineType,
                      'wbx-button--disabled':
                        !available_engines.includes(engine),
                    },
                  }"
                  @click="
                    selectEngine(engine);
                    trackFeatureSelection(
                      `engine_type.${engine}`,
                      vehicle ?? null,
                    );
                  "
                >
                  {{ getEngineTypeTranslation(engine) }}
                </WbButtonGroupItem>
              </WbButtonGroup>
            </div>
          </div>
        </div>

        <!-- Battery Capacity Selector -->
        <div
          class="wb-grid-col wb-margin-bottom-3xs batterycapacity-selector wb-grid-col-mq1-12 wb-grid-col-mq3-12 wb-grid-col-mq4-6"
          v-if="batteryCapacities.length > 1"
        >
          <div class="owvans-technical-data-wrapper__column-desktop">
            <span class="owvans-technical-data-wrapper__column-text wb-text-s">
              {{
                getComponentLabel(
                  'owvc_tech_data_batteryCapacity',
                  'batteryCapacity',
                )
              }}
            </span>
            <div class="owvans-technical-data-wrapper__button-group">
              <WbButtonGroup
                variant="radio"
                :ssrAttrs="{ class: 'wbx-button-group' }"
              >
                <WbButtonGroupItem
                  v-for="(batteryCapacity, index) in batteryCapacities"
                  :key="index"
                  variant="secondary"
                  :selected="
                    batteryCapacity === vehicle?.batteryCapacity || undefined
                  "
                  :disabled="
                    !available_batteryCapacities.includes(batteryCapacity) ||
                    undefined
                  "
                  :data-test-id="`owvans-tech-data-batteryCapacity-desktop-${batteryCapacity}`"
                  :ssrAttrs="{
                    class: {
                      'wbx-button wbx-button--large wbx-button--secondary': true,
                      'wbx-button--selected':
                        batteryCapacity === vehicle?.batteryCapacity,
                      'wbx-button--disabled':
                        !available_batteryCapacities.includes(batteryCapacity),
                    },
                  }"
                  @click="
                    selectBatteryCapacity(batteryCapacity);
                    trackFeatureSelection(
                      `battery_capacity.${batteryCapacity}`,
                      vehicle ?? null,
                    );
                  "
                >
                  {{ batteryCapacity }}
                </WbButtonGroupItem>
              </WbButtonGroup>
            </div>
          </div>
        </div>

        <!-- Weight Selector -->
        <div
          class="wb-grid-col wb-margin-bottom-3xs weight-selector wb-grid-col-mq1-12 wb-grid-col-mq3-12 wb-grid-col-mq4-6"
          v-if="weights.length > 1"
        >
          <div class="owvans-technical-data-wrapper__column-desktop">
            <span class="owvans-technical-data-wrapper__column-text wb-text-s">
              {{ getComponentLabel('owvc_tech_data_weight', 'weight') }}
            </span>
            <div class="owvans-technical-data-wrapper__button-group">
              <WbButtonGroup
                variant="radio"
                :ssrAttrs="{ class: 'wbx-button-group' }"
              >
                <WbButtonGroupItem
                  v-for="(weight, index) in weights"
                  :key="index"
                  variant="secondary"
                  :selected="
                    weight === vehicle?.weights?.weightMax?.value || undefined
                  "
                  :disabled="!available_weights.includes(weight) || undefined"
                  :data-test-id="`owvans-tech-data-weights-desktop-${weight}`"
                  :ssrAttrs="{
                    class: {
                      'wbx-button wbx-button--large wbx-button--secondary': true,
                      'wbx-button--selected':
                        weight === vehicle?.weights?.weightMax?.value,
                      'wbx-button--disabled':
                        !available_weights.includes(weight),
                    },
                  }"
                  @click="
                    selectWeight(weight);
                    trackFeatureSelection(`weight.${weight}`, vehicle ?? null);
                  "
                >
                  {{ weight }}
                </WbButtonGroupItem>
              </WbButtonGroup>
            </div>
          </div>
        </div>

        <!-- Drivetrain Selector -->
        <div
          class="wb-grid-col wb-margin-bottom-3xs weight-selector wb-grid-col-mq1-12 wb-grid-col-mq3-12 wb-grid-col-mq4-6"
          v-if="drivetrains.length > 1"
        >
          <div class="owvans-technical-data-wrapper__column-desktop">
            <span class="span-s owvans-technical-data-wrapper__column-text">
              {{
                getComponentLabel(
                  'owvc_tech_data_drivetrainType',
                  'drivetrainType',
                )
              }}
            </span>
            <div class="owvans-technical-data-wrapper__button-group">
              <WbButtonGroup
                variant="radio"
                :ssrAttrs="{ class: 'wbx-button-group' }"
              >
                <WbButtonGroupItem
                  v-for="(train, index) in drivetrains"
                  :key="index"
                  variant="secondary"
                  :selected="train === vehicle?.drivetrainType || undefined"
                  :disabled="
                    !available_drivetrains.includes(train) || undefined
                  "
                  :data-test-id="`owvans-tech-data-drivetrains-desktop-${train}`"
                  :ssrAttrs="{
                    class: {
                      'wbx-button wbx-button--large wbx-button--secondary': true,
                      'wbx-button--selected': train === vehicle?.drivetrainType,
                      'wbx-button--disabled':
                        !available_drivetrains.includes(train),
                    },
                  }"
                  @click="
                    selectDrivetrain(train);
                    trackFeatureSelection(
                      `drivetrain.${train}`,
                      vehicle ?? null,
                    );
                  "
                >
                  {{ getDrivetrainTranslation(train) }}
                </WbButtonGroupItem>
              </WbButtonGroup>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@workbench/core/dist/scss/utility.scss';
.owvans-technical-data-wrapper {
  &__column-text {
    color: var(--wb-grey-20);
    display: -ms-flexbox;
    font-family: var(--wb-font-text);
    font-size: 1rem;
    font-style: normal;
    font-weight: 700;
  }
  .wb-grid-row {
    grid-gap: 16px;
  }

  .wb-grid-col {
    width: 100%;
  }
}
.owvans-technical-data-wrapper__button-group {
  @include wb-spacing('margin-top', 'xxs');
  overflow-y: hidden;
}
.wbx-button-group,
.wb-button-group {
  width: 100%;
  white-space: nowrap;

  .wbx-button-group-item,
  .wb-button-group-item {
    flex-grow: 1;
    flex-shrink: 1;
  }
}
</style>
